import type { ComponentType } from "react";
import {
  CleanCoreDestinationDetail,
  EngineeringObjectsDetail,
} from "./cleanCore/CleanCoreDetail";

import type { ActId } from "./acts";
import type { DiagnosticTabProps } from "./DiagnosticSection";
import { ActiveUsersDetail } from "./activeUsers/ActiveUsersDetail";
import { AdherenceDetail } from "./adherence/AdherenceDetail";
import { BomDetails } from "./bom/BomDetails";
import { BomQuantities } from "./bom/BomQuantities";
import { ChartOfAccountsDetail } from "./chartOfAccounts/ChartOfAccountsDetail";
import { ModuleCustomizationsDetail } from "./moduleCustomizations/ModuleCustomizationsDetail";
import { TechnicalUsersDetail } from "./digitalAccess/TechnicalUsersDetail";
import { OrgStructureDetail } from "./orgStructure/OrgStructureDetail";
import { ProcessDestinationDetail } from "./processFit/ProcessDestinationDetail";
import { ProcessFitDetail } from "./processFit/ProcessFitDetail";
import { ScopeGapsDetail } from "./scopeGaps/ScopeGapsDetail";
import { ScopeItemsDetail } from "./scopeItems/ScopeItemsDetail";
import { WricefDetail } from "./wricef/WricefDetail";

/**
 * Uma tela de detalhe do Diagnóstico, aberta a partir de uma célula de um ato.
 *
 * O registro existe para que cada EF acrescente uma linha aqui, em vez de inventar
 * a própria navegação. Quem monta rota, breadcrumb e botão de voltar é a tela do
 * Diagnóstico; a entrada só diz o que é e onde nasceu.
 */
export interface DiagnosticDetail {
  /** O trecho da URL depois de `/diagnostic/`. Estável, em inglês, nunca vai para a tela. */
  slug: string;
  /** O ato em que a célula de origem vive, e para onde "Voltar ao diagnóstico" devolve. */
  originAct: ActId;
  /** Chave de i18n do título, que fecha o breadcrumb. */
  title: string;
  /**
   * Conteúdo que não pode aparecer antes da revelação do Ato 5. Vale também para
   * quem chega por link direto: colar a URL não pode entregar o número antes da hora.
   * Detalhe com `originAct` no Ato 5 tranca mesmo com `false` aqui.
   */
  requiresReveal: boolean;
  /**
   * Recebe os mesmos dados que os atos, por props. A tela do Diagnóstico continua
   * sendo a única a chamar `useDiagnostic`, e o detalhe fica apresentacional.
   */
  Component: ComponentType<DiagnosticTabProps>;
}

/**
 * Cada EF (EF-DIAG-002, EF-FUNC-003/004/005 e EF-ARCH-001) acrescenta a sua entrada
 * na própria PR.
 */
export const DIAGNOSTIC_DETAILS: readonly DiagnosticDetail[] = [
  // EF-DIAG-002 (#1342): aberto pelo tile "Empresas" do Ato 1. Sem trava de
  // revelação: a tela não mostra valor financeiro.
  {
    slug: "org-structure",
    originAct: "environment",
    title: "Organizational structure",
    requiresReveal: false,
    Component: OrgStructureDetail,
  },
  // EF-FUNC-009 (#1374 P2): aberto pelo quadro do plano de contas do Ato 1, já
  // filtrado pelo status ou pela ação clicada. Sem valor financeiro.
  {
    slug: "chart-of-accounts",
    originAct: "environment",
    title: "Chart of accounts adherence",
    requiresReveal: false,
    Component: ChartOfAccountsDetail,
  },
  // EF-FUNC-005 (#1346 fatia 10): aberto pelo quadro do Digital Access do Ato 1.
  // Sem valor financeiro: documento, e não bloco nem preço.
  {
    slug: "technical-users",
    originAct: "environment",
    title: "Technical users that created documents",
    requiresReveal: false,
    Component: TechnicalUsersDetail,
  },
  // #1478: aberto pela caixa "Usuários ativos" e pelo quadro de perfil de uso do
  // Ato 1. Usuário, tipo, papéis e SKU sugerido, sem valor: sem trava de revelação.
  {
    slug: "active-users",
    originAct: "environment",
    title: "Active users",
    requiresReveal: false,
    Component: ActiveUsersDetail,
  },
  // EF-FUNC-004 (#1344): aberto pelo tile "Scope items ativos" do Ato 1. Sem trava
  // de revelação: a lista não mostra valor financeiro.
  {
    slug: "scope-items",
    originAct: "environment",
    title: "Active scope items",
    requiresReveal: false,
    Component: ScopeItemsDetail,
  },
  // EF-FUNC-011 (RF-08): aberto pelo tile e pelo frame "Aderência ao padrão" do
  // Ato 1. Três abas, os GAPs só para leitura. Sem valor financeiro.
  {
    slug: "adherence",
    originAct: "environment",
    title: "Standard fit",
    requiresReveal: false,
    Component: AdherenceDetail,
  },
  // #1479: aberto pelo card de gaps do Ato 2, sem filtro ou já filtrado pela
  // categoria clicada. Sem valor financeiro: o preço do gap é do Ato 5.
  {
    slug: "scope-gaps",
    originAct: "diagnosis",
    title: "Gaps by category",
    requiresReveal: false,
    Component: ScopeGapsDetail,
  },
  // Pedido do David em 19/10/2026: o card "Customizações por módulo" do Ato 2 abre
  // os objetos de cada módulo, na linguagem do card. Sem valor financeiro.
  {
    slug: "module-customizations",
    originAct: "diagnosis",
    title: "Customisations by module",
    requiresReveal: false,
    Component: ModuleCustomizationsDetail,
  },
  // Detalhes do Ato 2 pedidos pelo David em 19/10/2026: o card "Aderência
  // Fit-to-Standard por processo" e o "Destino provável dos processos", cada um
  // com o título do card. Sem valor financeiro.
  {
    slug: "process-fit",
    originAct: "diagnosis",
    title: "Fit-to-Standard by process",
    requiresReveal: false,
    Component: ProcessFitDetail,
  },
  {
    slug: "process-destination",
    originAct: "diagnosis",
    title: "Likely destination of the processes",
    requiresReveal: false,
    Component: ProcessDestinationDetail,
  },
  // EF-ARCH-001 (#1477, RF-08 da #1343): aberto pelo card WRICEF do Ato 1, por
  // "Ver itens" ou pela legenda, já filtrado pela decisão. Sem valor financeiro.
  {
    slug: "wricef",
    originAct: "environment",
    title: "Custom code by type (WRICEF)",
    requiresReveal: false,
    Component: WricefDetail,
  },
  // Pedido do David em 19/10/2026: os números do card "Complexidade e decisões" e a
  // legenda do "Destino do código Z sob Clean Core" do Ato 2 abrem o que contam.
  // Sem valor financeiro, sem trava de revelação.
  {
    slug: "engineering-objects",
    originAct: "diagnosis",
    title: "Objects needing engineering",
    requiresReveal: false,
    Component: EngineeringObjectsDetail,
  },
  {
    slug: "clean-core-destination",
    originAct: "diagnosis",
    title: "Where the Z code goes under Clean Core",
    requiresReveal: false,
    Component: CleanCoreDestinationDetail,
  },
  // EF-FUNC-003 (#1345, F4): aberto pelo card da BOM do Pricing App do Ato 5, para
  // ajustar a quantidade e salvar como versão nova. Mostra valor: só depois da
  // revelação do Ato 5, também para quem chega por link direto.
  {
    slug: "bom-details",
    originAct: "investment",
    title: "BOM details",
    requiresReveal: true,
    Component: BomDetails,
  },
  // EF-FUNC-003 (#1345, F3): aberto pelo quadro de perfil de uso do Ato 1. Só as
  // quantidades da BOM vigente, sem valor: não espera a revelação, e o "Voltar"
  // devolve ao Ato 1. Valor e ajuste ficam no `bom-details`, que nasce no Ato 5.
  {
    slug: "bom-quantities",
    originAct: "environment",
    title: "BOM quantities",
    requiresReveal: false,
    Component: BomQuantities,
  },
];
